/**
 * The states of the custom pack lab the Design Constructor preview can open on, shared by the constructor's
 * "Lab state" picker and the iframe, which seeds each one (`customPack/preview/labPreviewScenes.ts`).
 *
 * `live` is the lab as it is today. For a real custom pack the constructor then asks for no state at all, so the
 * preview runs the real lab; on the demo pack `live` is the same lab on demo data, nothing seeded. Every other state
 * runs on a client that answers in place of the server and never sends it anything that writes.
 */

/** The query parameter the preview's address carries the state in. */
export const LAB_SCENE_PARAM = 'lab_scene';

export type LabPreviewSceneGroup = 'Catalog' | 'Odds' | 'Reveal' | 'Other';

export interface LabPreviewScene {
  id: string;
  label: string;
  group: LabPreviewSceneGroup;
}

export const LAB_PREVIEW_SCENE_GROUPS: readonly LabPreviewSceneGroup[] = ['Catalog', 'Odds', 'Reveal', 'Other'];

export const LAB_PREVIEW_SCENES = [
  { id: 'live', label: 'Live — the real lab', group: 'Catalog' },
  { id: 'catalog-empty', label: 'Nothing picked yet', group: 'Catalog' },
  { id: 'catalog-loading', label: 'Cards loading', group: 'Catalog' },
  { id: 'catalog-picked', label: 'A few cards picked', group: 'Catalog' },
  { id: 'catalog-ready', label: 'Pack ready', group: 'Catalog' },
  { id: 'catalog-full', label: 'Pack full', group: 'Catalog' },
  { id: 'catalog-no-results', label: 'No cards found', group: 'Catalog' },
  { id: 'catalog-error', label: "Cards couldn't load", group: 'Catalog' },
  { id: 'odds', label: 'Set odds (one card locked)', group: 'Odds' },
  { id: 'odds-add-cards', label: 'Needs more cards', group: 'Odds' },
  { id: 'odds-cant-reach', label: "Odds can't reach 100%", group: 'Odds' },
  { id: 'odds-gone', label: 'A card is gone', group: 'Odds' },
  { id: 'odds-error', label: 'Rip failed', group: 'Odds' },
  { id: 'odds-purchasing', label: 'Ripping…', group: 'Odds' },
  { id: 'odds-price-changed', label: 'Price changed', group: 'Odds' },
  { id: 'reveal-loading', label: 'Order loading', group: 'Reveal' },
  { id: 'reveal-opening', label: 'Pack opening (your ceremony)', group: 'Reveal' },
  { id: 'reveal-winner', label: 'Winner with a buyback', group: 'Reveal' },
  { id: 'reveal-winner-no-buyback', label: 'Winner, no buyback', group: 'Reveal' },
  { id: 'reveal-selling', label: 'Selling back', group: 'Reveal' },
  { id: 'reveal-sold', label: 'Sold back', group: 'Reveal' },
  { id: 'reveal-vaulted', label: 'Kept in the vault', group: 'Reveal' },
  { id: 'reveal-error', label: "Order couldn't load", group: 'Reveal' },
  { id: 'reveal-open-failed', label: 'Opening fails', group: 'Reveal' },
  { id: 'unavailable', label: 'Pack unavailable', group: 'Other' },
] as const satisfies readonly LabPreviewScene[];

export type LabPreviewSceneId = (typeof LAB_PREVIEW_SCENES)[number]['id'];

/** Today's lab: the picker's default. */
export const LIVE_LAB_SCENE: LabPreviewSceneId = 'live';

const SCENE_IDS: ReadonlySet<string> = new Set(LAB_PREVIEW_SCENES.map((scene) => scene.id));

export function isLabPreviewSceneId(value: unknown): value is LabPreviewSceneId {
  return typeof value === 'string' && SCENE_IDS.has(value);
}

/** The scenes of one group, in the picker's order. */
export function labPreviewScenesOf(group: LabPreviewSceneGroup): readonly LabPreviewScene[] {
  return LAB_PREVIEW_SCENES.filter((scene) => scene.group === group);
}
